import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext.js";
import { toast } from "sonner";
import axios from "axios";
import API from "@/utils/api";
import { useEffect } from "react";

import Logo from "@/components/ui/Logo";

// Debug logging
if (typeof window !== 'undefined') {
  console.log('[LoginPage] API URL:', API);
  console.log('[LoginPage] Is iframe:', window.self !== window.top);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setUser, user } = useAuth();

  // Try to use a neutral logo for the tenant or the general Conexo brand if not available.

  // Redirect if already logged in or after successful login
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/config`);
        const data = response.data;
        const color = data.primary_color || data.branding?.primary_color;
        if (color) {
          document.documentElement.style.setProperty('--primary', color);
          document.documentElement.style.setProperty('--accent', color);
        }

        // Update Title and Favicon
        const name = data.restaurant_name || data.name;
        if (name) {
          document.title = `${name} — Identificarse`;
        }
        
        const logo = data.logo || data.branding?.logo_url;
        if (logo) {
          const favicon = document.querySelector('link[rel="icon"]');
          if (favicon) favicon.href = logo;
        }
      } catch (e) {
        console.error("Error fetching branding in LoginPage:", e);
      }
    };
    fetchBranding();

    if (user && user.user_id) {
      const userRole = user.role;
      let redirectTo = location.state?.from || (userRole === "customer" ? "/menu" : "/admin");

      // Role-based redirection overrides
      if (userRole === "admin" || userRole === "support") {
        redirectTo = "/admin";
      } else if (userRole === "cashier") {
        redirectTo = "/admin/pos";
      } else if (userRole === "kitchen") {
        redirectTo = "/admin/kitchen";
      } else if (userRole === "driver") {
        redirectTo = "/driver";
      }

      console.log(`[LoginPage] User detected (${userRole}), redirecting to ${redirectTo}`);
      navigate(redirectTo, { replace: true });
    }
  }, [user, navigate, location.state]);

  const [mode, setMode] = useState("login"); // login, register
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: ""
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name, phone: formData.phone || null };

      const response = await axios.post(`${API}${endpoint}`, payload, {
        withCredentials: true,
        timeout: 10000 // 10 segundos de timeout
      });

      // Store token
      if (response.data.access_token) {
        localStorage.setItem("session_token", response.data.access_token);
      }

      // Set user in context
      setUser(response.data.user);

      toast.success(mode === "login" ? "¡Bienvenido de nuevo!" : "¡Cuenta creada exitosamente!");

      // Redirect based on user role
      const userRole = response.data.user?.role;
      let redirectTo = location.state?.from || "/menu";

      // Role-based redirection
      if (userRole === "admin" || userRole === "support") {
        redirectTo = "/admin";
      } else if (userRole === "cashier") {
        redirectTo = "/admin/pos";
      } else if (userRole === "kitchen") {
        redirectTo = "/admin/kitchen";
      } else if (userRole === "driver") {
        redirectTo = "/driver";
      }

      navigate(redirectTo, { replace: true });

    } catch (error) {
      const message = error.response?.data?.detail || "Error de autenticación";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="px-4 py-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="login-back-btn"
          className="text-white hover:bg-gray-800"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
      </div>

      <div className="px-6 py-8 flex flex-col items-center max-w-md mx-auto">
        {/* Logo */}
        <div className="mb-6 flex flex-col items-center justify-center p-6 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl shadow-2xl">
          <Logo size={40} variant="icon" />
        </div>

        <h1 className="font-bold text-3xl mb-2 text-white text-center">
          {mode === "login" ? "Bienvenido" : "Crear cuenta"}
        </h1>
        <p className="text-gray-400 text-center mb-6">
          {mode === "login"
            ? "Inicia sesión para hacer pedidos"
            : "Regístrate para comenzar a pedir"
          }
        </p>

        {/* Email/Password Form */}
        <form onSubmit={handleEmailAuth} className="w-full space-y-4 mb-6">
          {mode === "register" && (
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="text"
                name="name"
                placeholder="Nombre completo"
                value={formData.name}
                onChange={handleChange}
                required={mode === "register"}
                className="pl-10 h-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                data-testid="register-name-input"
              />
            </div>
          )}

          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
              className="pl-10 h-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
              data-testid="email-input"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Contraseña"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              className="pl-10 pr-10 h-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {mode === "register" && (
            <div className="relative">
              <Input
                type="tel"
                name="phone"
                placeholder="Teléfono (opcional)"
                value={formData.phone}
                onChange={handleChange}
                className="pl-4 h-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                data-testid="register-phone-input"
              />
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full h-12 rounded-full font-bold bg-primary hover:opacity-90 text-black"
            style={{ backgroundColor: 'var(--primary)' }}
            disabled={isLoading}
            data-testid="email-auth-btn"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : mode === "login" ? (
              "Iniciar sesión"
            ) : (
              "Crear cuenta"
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center w-full mb-6">
          <div className="flex-1 border-t border-gray-700"></div>
          <span className="px-4 text-gray-500 text-sm">o continúa con</span>
          <div className="flex-1 border-t border-gray-700"></div>
        </div>

        {/* Google Login Button */}
        <div className="w-full relative group">
          <Button
            type="button"
            size="lg"
            variant="outline"
            className="w-full h-12 rounded-full font-bold text-base gap-3 border-gray-700 text-white hover:bg-gray-800"
            onClick={login}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continuar con Google
          </Button>
        </div>

        {/* Guest Login for presentation convenience */}
        <button
          type="button"
          onClick={() => {
            navigate(-1);
            toast.info("Continuando como invitado...");
          }}
          className="text-gray-500 hover:text-primary text-[11px] font-bold uppercase tracking-widest mt-4 transition-colors"
          style={{ '--hover-color': 'var(--primary)' }}
        >
          Continuar como invitado →
        </button>

        {/* Toggle mode */}
        <p className="text-gray-400 text-center mt-6">
          {mode === "login" ? (
            <>
              ¿No tienes cuenta?{" "}
              <button
                type="button"
                onClick={() => setMode("register")}
                className="text-primary font-medium hover:underline"
                style={{ color: 'var(--primary)' }}
                data-testid="switch-to-register"
              >
                Regístrate
              </button>
            </>
          ) : (
            <>
              ¿Ya tienes cuenta?{" "}
              <button
                type="button"
                onClick={() => setMode("login")}
                className="text-primary font-medium hover:underline"
                style={{ color: 'var(--primary)' }}
                data-testid="switch-to-login"
              >
                Inicia sesión
              </button>
            </>
          )}
        </p>

        <p className="text-xs text-gray-500 text-center mt-4 max-w-sm">
          Al continuar, aceptas nuestros términos de servicio y política de privacidad
        </p>
      </div>
    </div>
  );
}
